import { createContext, useContext, useState, useMemo, useRef, type ReactNode } from 'react';
import { useMultiplayer } from '../hooks/useMultiplayer';
import { useAuth } from '../hooks/useAuth';
import { usePages } from '../hooks/usePages';
import type { SessionSettings, GameSystemId } from '@roll1/shared';

type UseMultiplayerReturn = ReturnType<typeof useMultiplayer>;
type UseAuthReturn = ReturnType<typeof useAuth>;
type UsePagesReturn = ReturnType<typeof usePages>;

export interface SessionContextValue {
  auth: UseAuthReturn;
  mp: UseMultiplayerReturn;
  pages: UsePagesReturn;
  isGM: boolean;
  outgoingPageId: string;
  gameSystem: GameSystemId;
  sessionSettings: SessionSettings;
  setSessionSettings: React.Dispatch<React.SetStateAction<SessionSettings>>;
}

/** Exported for test harnesses that provide a session without the network. */
export const SessionContext = createContext<SessionContextValue | null>(null);

export function SessionProvider({ children }: { children: ReactNode }) {
  const auth = useAuth();
  const mp = useMultiplayer();
  const pages = usePages(mp.session?.id);
  const [sessionSettings, setSessionSettings] = useState<SessionSettings>({
    measurementMode: 'simple',
    gridSizeFt: 5,
    playerAiEnabled: true,
    playerAiTokenBudgetPerHour: null,
    playerAiRateLimitPerMinute: 10,
    aiAssistantName: 'Tiah',
    aiMentionEnabled: true,
    aiMentionRateLimitPerMinute: 5,
    imageGenModel: 'flux-schnell',
    imageGenStylePrefix: '',
    imageGenRateLimitPerHour: 10,
    // v31: Display
    gridStyle: 'lines',
    gridOpacity: 100,
    defaultTokenSize: 'medium',
    hpBarVisibility: 'hover',
    // v32: Combat rules
    autoRollInitiative: false,
    confirmDamage: false,
    dnd5e: { critRange: 20, deathSavesEnabled: true },
    // v33: Permissions
    playerPermissions: {
      moveOtherTokens: false,
      drawOnMap: true,
      viewOtherSheets: 'basic',
      createNotes: true,
      useAi: true,
      addTokens: false,
    },
    // v34: Ambient overlay
    ambientOverlayEnabled: true,
    // v37: Custom stickers
    allowPlayerStickers: false,
  });

  const isGM = !mp.session || mp.participant?.role === 'gm';
  const outgoingPageId = pages.viewingPageId;
  const gameSystem: GameSystemId = mp.serverState?.gameSystem ?? 'dnd5e';

  const value = useMemo(
    () => ({ auth, mp, pages, isGM, outgoingPageId, gameSystem, sessionSettings, setSessionSettings }),
    [auth, mp, pages, isGM, outgoingPageId, gameSystem, sessionSettings, setSessionSettings],
  );

  return (
    <SessionContext.Provider value={value}>
      {children}
    </SessionContext.Provider>
  );
}

export function useSessionContext() {
  const ctx = useContext(SessionContext);
  if (!ctx) throw new Error('useSessionContext must be used within SessionProvider');
  return ctx;
}

/**
 * A subtree that views and edits ONE page on its own — a GM Studio map pane
 * (package 5.3). Same campaign (WS, page store, players' map, roles, settings),
 * but inside it the viewed page and the edit target are the pane's page:
 * `pages.viewingPageId` / `outgoingPageId` = `pageId`, and `hydratedPageIdRef`
 * is the pane's own (the page ITS canvas hooks hold). Everything that reads
 * "the current page" — at send time too (undo, batched erase, AoE) — reads the
 * pane's page, never the table's view or the last-opened pane.
 *
 * The table's own view is not the pane's to change: "look at another page"
 * inside the pane opens it in the workspace (`onOpenPage`) instead.
 */
export function PageViewScope({
  pageId,
  onOpenPage,
  children,
}: {
  pageId: string;
  onOpenPage?: (pageId: string) => void;
  children: ReactNode;
}) {
  const parent = useSessionContext();
  const hydratedPageIdRef = useRef('');
  const openRef = useRef(onOpenPage);
  openRef.current = onOpenPage;

  const parentPages = parent.pages;
  const pages = useMemo<UsePagesReturn>(() => {
    const open = (id: string): void => {
      if (id !== '' && id !== pageId) openRef.current?.(id);
    };
    return {
      ...parentPages,
      viewingPageId: pageId,
      isPreview: pageId !== parentPages.activePageId,
      hydratedPageIdRef,
      previewPage: open,
      returnToActive: () => open(parentPages.activePageId),
      setViewingPageId: (next) => open(typeof next === 'function' ? next(pageId) : next),
    };
  }, [parentPages, pageId]);

  const value = useMemo(
    () => ({ ...parent, pages, outgoingPageId: pageId }),
    [parent, pages, pageId],
  );

  return (
    <SessionContext.Provider value={value}>
      {children}
    </SessionContext.Provider>
  );
}

/**
 * Non-throwing variant for components that ALSO render outside the session
 * tree (demo mode, unit tests render sections without providers). Returns
 * null there; callers degrade to their providerless behavior.
 */
export function useOptionalSessionContext(): SessionContextValue | null {
  return useContext(SessionContext);
}
